import { useState } from 'react';
import { Modal, ModalBody, Button, FormGroup, Label, Input, Alert } from 'reactstrap';
import { userLogin } from '../../services/auth';

type SessionExpiryModalProps = {
    isOpen: boolean;
    isExpired: boolean;
    userEmail: string;
    onReAuthenticated: (resp: any) => void;
};

export const SessionExpiryModal = ({
    isOpen,
    isExpired,
    userEmail,
    onReAuthenticated,
}: SessionExpiryModalProps) => {
    const [username, setUsername] = useState(userEmail);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError(null);
        setSubmitting(true);

        try {
            const resp = await userLogin(username, password);
            if (!resp.user) {
                setAuthError('Incorrect credentials. Please try again.');
                setSubmitting(false);
                return;
            }

            // Store updated expiry/TTL
            if (resp.expiry) {
                localStorage.setItem('expiry', resp.expiry);
                const ttl = new Date(resp.expiry).getTime() - Date.now();
                localStorage.setItem('sessionTTL', ttl.toString());
            }
            localStorage.setItem('user', JSON.stringify(resp.user));

            setPassword('');
            setSubmitting(false);
            onReAuthenticated(resp);
        } catch (err) {
            setAuthError('An error occurred. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <Modal
            centered
            isOpen={isOpen}
            backdrop='static'
            keyboard={false}
            size='md'
        >
            <ModalBody className='p-4'>
                <div className='text-center mb-4'>
                    <h3 className='text-modal modal__title'>
                        {isExpired ? 'Session Expired' : 'Session Expiring Soon'}
                    </h3>
                    <p className='text-muted'>
                        {isExpired
                            ? 'Your session has expired. Please sign in again to continue.'
                            : 'Your session is about to expire. Sign in to continue working.'}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label for='session-username'>Email Address</Label>
                        <Input
                            id='session-username'
                            type='email'
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for='session-password'>Password</Label>
                        <div style={{ position: 'relative' }}>
                            <Input
                                id='session-password'
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '10px',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    color: '#6c757d',
                                }}
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </span>
                        </div>
                    </FormGroup>

                    {authError && (
                        <Alert color='danger' className='py-2'>
                            {authError}
                        </Alert>
                    )}

                    <div className='text-center'>
                        <Button
                            color='primary'
                            type='submit'
                            disabled={submitting}
                        >
                            {submitting ? 'Signing in...' : 'Sign In'}
                        </Button>
                    </div>
                </form>
            </ModalBody>
        </Modal>
    );
};
